import { Campaign } from '@app/hooks/merkl/useMerklCampaigns';
import { differenceInCalendarDays } from 'date-fns';
import { formatUnits } from 'ethers/lib/utils';
import { useMemo } from 'react';

export type MarketMakerRewards = {
  totalRewards: number;
  dailyRewards: number;
  usdDailyRewards: number;
  rewardsToken: {
    id: string;
    decimals: number;
    symbol: string;
  };
};

export function useMarketMakerRewards(
  campaigns: Campaign[] | undefined,
  nitroPoolId: string | undefined
) {
  return useMemo(() => {
    if (!campaigns) {
      return {
        totalNitroRewardsUSD: 0,
        totalPoolRewardsUSD: 0,
        nitroRewards: [],
        poolRewards: []
      };
    }

    const nitroRewards: MarketMakerRewards[] = [];
    const poolRewards: MarketMakerRewards[] = [];

    campaigns
      .filter(c => {
        if (nitroPoolId) {
          return (
            c.campaignParameters.whitelist
              .map(w => w.toLowerCase())
              .includes(nitroPoolId?.toLowerCase()) ||
            c.campaignParameters.whitelist.length === 0
          );
        }

        return c.campaignParameters.whitelist.length === 0;
      })
      .forEach(campaign => {
        const isNitro = campaign.campaignParameters.whitelist.length > 0;

        const daysDifference = Math.abs(
          differenceInCalendarDays(
            new Date(campaign.startTimestamp * 1000),
            new Date(campaign.endTimestamp * 1000)
          )
        );

        const amount = Number(
          formatUnits(
            campaign.amount,
            campaign.campaignParameters.decimalsRewardToken
          )
        );

        const dailyRewards = amount / daysDifference;

        const usdDailyRewards = campaign.priceRewardToken
          ? dailyRewards * +campaign.priceRewardToken
          : 0;

        const rewardsData = {
          totalRewards: amount,
          dailyRewards,
          usdDailyRewards,
          rewardsToken: {
            id: campaign.rewardToken,
            decimals: campaign.campaignParameters.decimalsRewardToken,
            symbol: campaign.campaignParameters.symbolRewardToken
          }
        };

        if (
          isNitro &&
          nitroPoolId &&
          campaign.campaignParameters.whitelist
            .map(w => w.toLowerCase())
            .includes(nitroPoolId.toLowerCase())
        ) {
          nitroRewards.push(rewardsData);
        } else {
          poolRewards.push(rewardsData);
        }
      });

    const totalNitroRewardsUSD = nitroRewards.reduce((res, item) => {
      return res + item.usdDailyRewards;
    }, 0);

    const totalPoolRewardsUSD = poolRewards.reduce((res, item) => {
      return res + item.usdDailyRewards;
    }, 0);

    return {
      totalNitroRewardsUSD,
      totalPoolRewardsUSD,
      nitroRewards,
      poolRewards
    };
  }, [campaigns, nitroPoolId]);
}
